'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Facebook, ShieldCheck } from 'lucide-react';
import { SITE_CONFIG, NAV_LINKS } from '@/data/site-constants';
import { NewsletterForm } from './newsletter-form';
import { BrandWordmark } from './brand-wordmark';

export function SiteFooter() {
  const t = useTranslations('public');
  return (
    <footer className="bg-eifo-primary text-white">
      <div className="mx-auto max-w-7xl px-4 py-12">
        {/* Main grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Column 1: Brand */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <Image src="/eifo-logo.png" alt="EiFO Academy" width={40} height={40} />
              <BrandWordmark className="text-lg" />
            </div>
            {/* "Education is Future Opportunity" is the EiFO motto and stays in
                English in every locale; only the descriptor after it translates. */}
            <p className="text-sm text-white/70">{t('footer.tagline')}</p>

            {/* Compliance badges — stacked and centered under the tagline. Each
                links to the authoritative government source. */}
            <div className="mt-4 flex flex-col items-center gap-2">
              <a
                href="https://www.ftc.gov/business-guidance/privacy-security/childrens-privacy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.coppaAria')}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-white/40 hover:bg-white/10 hover:text-white"
              >
                <ShieldCheck className="h-4 w-4 text-eifo-orange" />
                {t('footer.coppaCompliant')}
              </a>
              <a
                href="https://studentprivacy.ed.gov/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.ferpaAria')}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-white/40 hover:bg-white/10 hover:text-white"
              >
                <ShieldCheck className="h-4 w-4 text-eifo-orange" />
                {t('footer.ferpaCompliant')}
              </a>
            </div>
          </div>

          {/* Column 2: Contact */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider">{t('footer.getInTouch')}</h3>
            <address className="space-y-2 text-sm not-italic text-white/70">
              <p>{SITE_CONFIG.address}</p>
              {SITE_CONFIG.phones.map((p) => (
                <p key={p.number}>
                  <span className="text-white/50">{p.label}: </span>
                  <a href={`tel:${p.number.replace(/\s/g, '')}`} className="hover:text-white">
                    {p.number}
                  </a>
                </p>
              ))}
              <p>
                <a href={`mailto:${SITE_CONFIG.email}`} className="hover:text-white">
                  {SITE_CONFIG.email}
                </a>
              </p>
            </address>
          </div>

          {/* Column 3: Quick Links */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="text-white/70 hover:text-white">
                    {t(link.labelKey.replace('public.', ''))}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider">{t('footer.newsletter')}</h3>
            <p className="mb-3 text-sm text-white/70">
              {t('footer.newsletterBlurb')}
            </p>
            <NewsletterForm variant="dark" />
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
          <div className="flex items-center gap-4">
            <a
              href={SITE_CONFIG.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
          </div>
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
            <nav aria-label="Legal" className="flex items-center gap-4 text-xs text-white/60">
              <Link href="/privacy" className="hover:text-white">
                {t('footer.privacy')}
              </Link>
              <Link href="/terms" className="hover:text-white">
                {t('footer.terms')}
              </Link>
              <Link href="/refund-policy" className="hover:text-white">
                {t('footer.refundPolicy')}
              </Link>
            </nav>
            <p className="text-xs text-white/50">{SITE_CONFIG.copyright}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
